import { Callout } from '@blueprintjs/core'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { Switch } from 'react-router'
import { BrowserRouter as Router, Redirect, Route, withRouter } from 'react-router-dom'
import * as Sentry from '@sentry/browser'
import PageTracker from '../analytics/pageTracker'
import Nag from '../components/Nag'
import AnnouncementBanner from '../components/AnnouncementBanner'
import PasswordExpirationWarning from '../components/PasswordExpirationWarning'
import Navigation from '../components/Navigation'
import OnboardingRequired from '../components/OnboardingRequired'
import Spinner from '../components/Spinner'
import config from '../config'
import { usePermissions } from '../hooks/usePermissions'
import StoreAdminEdit from '../routes/admin/users/StoreAdminEdit'
import NotFound from '../routes/not-found'
import Settings from '../routes/settings'
import { AppState } from '../store'
import { RestrictedRoute } from './RestrictedRoute'
import { calculateHomeBehavior, lazyLoadWithRetry, reloadOnNewVersionEffect } from './utils'
import { regexMatchers } from '../hooks/usePermissionsMatchers'
import AdminNewOrEditUsers from '../routes/admin/users/NewOrEdit'
import { ExpressListStart } from '../routes/express_list/ExpressListStart'
import ExpressListForm from '../routes/express_list/ExpressListForm'
import { ConveyorPortProvider } from '../contexts/ConveyorPortContext'
import { ScanBooks } from '../routes/scan-books/ScanBooks'
import { ScanToListBooks } from '../routes/scan-to-list-books/ScanToListBooks'
import { NewListingPage } from '../routes/scan-to-list-books/NewListingPage'

const ShopGoodwillOnboarding = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "sgw-onboarding" */ '../routes/onboarding/ShopGoodwill')
)
const EditListing = lazyLoadWithRetry(() => import(/* webpackChunkName: "listings-edit" */ '../routes/listings/Edit'))
const EbayListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "ebay-listings-v4" */ '../routes/products_v4/listings/ebay')
)

const ShopifyListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "shopify-listings-v4" */ '../routes/products_v4/listings/shopify')
)

const ShopgoodwillListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "sgw-listings-v4" */ '../routes/products_v4/listings/shopgoodwill')
)
const FacebookListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "facebook-listings-v4" */ '../routes/products_v4/listings/facebook')
)
const GoodwillfindsListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "goodwillfinds-listings-v4" */ '../routes/products_v4/listings/goodwillfinds')
)
const EtsyListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "etsy-listings-v4" */ '../routes/products_v4/listings/etsy')
)

const AuthenticationManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "authentications" */ '../routes/authentications')
)
const NewItemAuthentication = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "new-item-authentications" */ '../routes/authentications/new')
)
const NewProductV5 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "products-v5" */ '../routes/products_v5/NewProduct')
)

const ManifestReports = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "manifest-reports" */ '../routes/manifests/reports')
)
const ManifestRoutes = lazyLoadWithRetry(() => import(/* webpackChunkName: "manifests" */ '../routes/manifests/routes'))
const ManifestScanFormRoutes = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "manifest-scan-forms" */ '../routes/manifest_scan_forms/routes')
)

const ProductManager2 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "productmanager2" */ '../routes/product_manager/v2')
)
const Reports = lazyLoadWithRetry(() => import(/* webpackChunkName: "reports" */ '../routes/reports/overview'))
const StoreReports = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "store-reports" */ '../routes/reports/stores/routes')
)
const UserReports = lazyLoadWithRetry(() => import(/* webpackChunkName: "user-reports" */ '../routes/reports/users'))
const AdminRoutes = lazyLoadWithRetry(() => import(/* webpackChunkName: "admin" */ '../routes/admin/routes'))
const PickLists = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "pick-lists" */ '../routes/orders/pick_lists/routes')
)

const ShowOrder = lazyLoadWithRetry(() => import(/* webpackChunkName: "orders-show" */ '../routes/orders/show/show'))
const EbayListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-ebay" */ '../routes/listing_manager/ebay')
)

const ShopgoodwillListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-sgw" */ '../routes/listing_manager/shopgoodwill')
)

const ShopifyListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-shopify" */ '../routes/listing_manager/shopify')
)

const GoodwillfindsListingManager = lazyLoadWithRetry(
  () =>
    import(
      /* webpackChunkName: "listing-manager-goodwillfinds" */ '../routes/listing_manager/goodwillfinds/GoodwillfindsListingManager'
    )
)

const EtsyListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-etsy" */ '../routes/listing_manager/etsy/EtsyListingManager')
)

const AmazonListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-amazon" */ '../routes/listing_manager/amazon/AmazonListingManager')
)

const ShopifyUnknownMarketRedirect = lazyLoadWithRetry(
  () =>
    import(
      /* webpackChunkName: "listing-manager-shopify" */ '../routes/listing_manager/shopify/ShopifyUnknownMarketRedirect'
    )
)

const EditProduct = lazyLoadWithRetry(() => import(/* webpackChunkName: "edit-product" */ '../routes/products_v5/Edit'))
const Orders = lazyLoadWithRetry(() => import(/* webpackChunkName: "orders-v2" */ '../routes/orders'))
const PickingProfilesNewOrEdit = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "picking-profiles" */ '../routes/picking_profiles/NewOrEdit')
)

const PickingProfilesPreview = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "picking-profiles-preview" */ '../routes/picking_profiles/Preview')
)

const StartPacking = lazyLoadWithRetry(() => import(/* webpackChunkName: "packing" */ '../routes/packing/routes'))
const Packing = lazyLoadWithRetry(() => import(/* webpackChunkName: "packing" */ '../routes/orders/packing/routes'))
const Shipments = lazyLoadWithRetry(() => import(/* webpackChunkName: "shipments" */ '../routes/shipments'))
const NewShipment = lazyLoadWithRetry(() => import(/* webpackChunkName: "shipments-new" */ '../routes/shipments/new'))

const AdminHome = lazyLoadWithRetry(() => import(/* webpackChunkName: "admin-home" */ '../routes/home/v2/AdminHome'))
const StoreAdminHome = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "store-admin-home" */ '../routes/home/v2/StoreAdminHome')
)
const ProductsCatalogHome = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "product-catalog" */ '../routes/products/ProductsCatalogHome')
)

const TopSales = lazyLoadWithRetry(() => import(/* webpackChunkName: "TopSales" */ '../routes/reports/v2/TopSales'))
const NewProductListing = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "NewProductListing" */ '../routes/products_v5/listings/New')
)

const OperationalProductivity = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "operationproductivity" */ '../routes/reports/productivity/Operational')
)

const ListingBulkReviewEbay = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-bulk-review-ebay" */ '../routes/listing_manager/bulk_review/ebay')
)

const ListingBulkReviewShopgoodwill = lazyLoadWithRetry(
  () =>
    import(
      /* webpackChunkName: "listing-bulk-review-shopgoodwill" */ '../routes/listing_manager/bulk_review/shopgoodwill'
    )
)

const ListingBulkReviewShopify = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-bulk-review-shopify" */ '../routes/listing_manager/bulk_review/shopify')
)
const ConveyorScreenTest = lazyLoadWithRetry(() => import('../routes/conveyor/view/ScreenTest'))

const LOAD_SPINNER_TIMEOUT = 100

function Loader() {
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => setLoading(true), LOAD_SPINNER_TIMEOUT)
    return () => clearTimeout(timer)
  }, [])

  if (loading)
    return (
      <div style={{ marginTop: 20 }}>
        <Spinner />
      </div>
    )

  return null
}

interface LastUpdatedInfo {
  updateAfter: Date
  serverVersion: string
}

const NUMBER_OF_DAYS_TO_DISMISS_NAG = 30

const App = ({ history }) => {
  const { permissions, includesAllPermissions, includesSomePermissions, matchesSomePermissions } = usePermissions()
  const { user, organization } = useSelector((s: AppState) => s.current)

  const [willReloadState, setWillReloadState] = React.useState<LastUpdatedInfo>({
    updateAfter: null,
    serverVersion: null
  })

  const { supplierId, email, role, onboarded, homepage } = user
  const orgConfigMarkets = window.orgConfig.markets

  React.useEffect(() => {
    const unlisten = history?.listen((_location, _action) => {
      // return early if the feature flag is off
      if (!config.RELOAD_FRONTEND_ON_UPDATE()) return

      try {
        const clientVersion = import.meta.env.VITE_SOURCE_VERSION
        if (!clientVersion) analytics.track('Versions Client Version Missing')

        const serverVersion = localStorage.getItem('serverVersion')
        if (!serverVersion) analytics.track('Versions Server Version Missing')

        reloadOnNewVersionEffect(clientVersion, serverVersion, willReloadState, setWillReloadState, config.PROD)
      } catch (e) {
        Sentry.captureException(e)
      }
    })
    return () => unlisten()
  }, [history, willReloadState])

  const { homeComponentString, redirectPath } = calculateHomeBehavior({
    permissions,
    homepageOverride: homepage,
    roleName: role,
    supplierId,
    userOnboarded: onboarded,
    orgConfigMarkets
  })

  let homeComponent = null
  switch (homeComponentString) {
    case 'AdminHome':
      homeComponent = AdminHome
      break
    case 'StoreAdminHome':
      homeComponent = StoreAdminHome
      break
    case 'ProductsCatalogHome':
      homeComponent = ProductsCatalogHome
      break
    default:
      homeComponent = null
  }

  const ebayMarketAccounts = organization.connectedMarkets.filter(market => market.name === 'ebay')
  const firstEbayMarketAccountId = ebayMarketAccounts.length >= 1 ? ebayMarketAccounts[0].marketAccountId : null

  return (
    <div className="app">
      <ConveyorPortProvider>
        {config.ANNOUNCEMENT_BANNER() && <AnnouncementBanner />}
        <Navigation />
        <PasswordExpirationWarning />
        {config.INCIDENT_IN_PROGRESS_STRING_FLAG() && (
          <Callout>
            {`${config.INCIDENT_IN_PROGRESS_STRING_FLAG()} Please check out the status page for more details: `}
            <a href="https://status.uprightlabs.com" target="_blank" rel="noreferrer">
              https://status.uprightlabs.com
            </a>
          </Callout>
        )}

        {config.SGW_OUTAGE() && config.current.organization.enabledMarkets.shopgoodwill && (
          <Callout>
            Shopgoodwill is experiencing issues with their API. Relisting, posting, and getting new orders might be
            delayed. For further status updates as we receive them please visit:{' '}
            <a href="https://status.uprightlabs.com" target="_blank" rel="noreferrer">
              https://status.uprightlabs.com
            </a>
          </Callout>
        )}

        {config.EASYPOST_OUTAGE() && (
          <Callout>
            Easypost is currently having an outage and may prevent you from generating shipping labels. Please check on
            the status <a href="https://www.easypoststatus.com/">https://www.easypoststatus.com/</a>
          </Callout>
        )}

        {config.FEDEX_OUTAGE() && (
          <Callout>FedEx is currently having an outage and may prevent you from generating shipping labels.</Callout>
        )}

        {(supplierId || includesSomePermissions('settings.team', 'settings.notifications')) && email === null && (
          // Show this nag to any user that can update team members, subscribe to notifications, or is a supplier
          <Nag
            calloutProps={{ intent: 'warning' }}
            nagKey="accountEmail"
            dismissForDays={NUMBER_OF_DAYS_TO_DISMISS_NAG}
          >
            <p>
              <b>Your email still needs to be configured.</b>
            </p>
            <p>Adding an email to your account allows you to login with your email and gives us a way to reach you.</p>

            <StoreAdminEdit />
          </Nag>
        )}
        <OnboardingRequired />
        <React.Suspense fallback={<Loader />}>
          <Switch>
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/shopgoodwill"
              component={ShopgoodwillListingManager}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/ebay_account/:market_account_id"
              // Force react to completely recreate the component when switching between ebay accounts,
              // otherwise we will keep showing data for the other market_account_id.
              // https://stackoverflow.com/a/49441836
              render={props => <EbayListingManager key={props.match.params.market_account_id} {...props} />}
            />
            <RestrictedRoute
              // Handle old bookmarks
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/ebay"
              render={() => {
                if (firstEbayMarketAccountId) {
                  // Regarding the index page
                  // (like http://localhost:3000/listings/ebay)
                  // For orgs with only a single ebay account, this works perfectly.
                  // For orgs with multiple ebay accounts, this gracefully shows them SOME ebay account until they update their bookmark.

                  // Regarding the show page:
                  // (like http://localhost:3000/listings/ebay?viewListingId=9)
                  // This will sometimes set the :market_account_id url param to the wrong ebay account.
                  // Not ideal for after they're done viewing the listing, but it still lets them navigate to the listing.
                  // This only happens until they refresh the page / update their individual listing bookmark.

                  return <Redirect to={`/listings/ebay_account/${firstEbayMarketAccountId}${window.location.search}`} />
                }
                return <NotFound />
              }}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/shopify_account/:market_account_id"
              component={ShopifyListingManager}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/shopify"
              component={ShopifyUnknownMarketRedirect}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/goodwillfinds"
              component={GoodwillfindsListingManager}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/etsy"
              component={EtsyListingManager}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/amazon"
              component={AmazonListingManager}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('settings.orders')}
              path="/picking_profiles/pick_list_preview"
              component={PickingProfilesPreview}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('settings.orders')}
              path="/picking_profiles/new"
              component={PickingProfilesNewOrEdit}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('settings.orders')}
              path="/picking_profiles/:id"
              component={PickingProfilesNewOrEdit}
            />

            {/* LEGACY */}
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              path="/listings/:id/edit"
              component={EditListing}
            />
            {/* END LEGACY */}
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.view')}
              exact
              path="/products"
              component={ProductManager2}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              exact
              path="/products/:id/listings/new"
              component={NewProductListing}
            />

            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post') && config.EXPRESS_LIST()}
              path="/express_list"
              exact
              component={ExpressListStart}
            />
            <RestrictedRoute
              key={history.location.key} // this makes the "New Product" nav bar button work when you've filled out half the product form, then you click "New Product"
              isPermissionGranted={permissions.includes('products.post')}
              path="/products/new"
              component={NewProductV5}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              path="/listings/ebay/:listingId/edit"
              component={EbayListingsV4}
              key="/listings/ebay/:listingId"
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/listings/shopgoodwill/:listingId"
              path="/listings/shopgoodwill/:listingId/edit"
              component={ShopgoodwillListingsV4}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/listings/shopify/:listingId"
              path="/listings/shopify/:listingId/edit"
              component={ShopifyListingsV4}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/listings/goodwillfinds/:listingId"
              path="/listings/goodwillfinds/:listingId/edit"
              component={GoodwillfindsListingsV4}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/listings/etsy/:listingId"
              path="/listings/etsy/:listingId/edit"
              component={EtsyListingsV4}
            />

            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post') && config.EXPRESS_LIST()}
              path="/express_list/:id"
              exact
              component={ExpressListForm}
            />

            <RestrictedRoute
              key={history.location.key} // this makes the "New Product" nav bar button work when you've filled out half the product form, then you click "New Product"
              isPermissionGranted={permissions.includes('products.post')}
              path="/products/new"
              component={NewProductV5}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              path="/listings/ebay/:listingId/edit"
              component={EbayListingsV4}
              key="/listings/ebay/:listingId"
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/listings/shopgoodwill/:listingId"
              path="/listings/shopgoodwill/:listingId/edit"
              component={ShopgoodwillListingsV4}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/listings/shopify/:listingId"
              path="/listings/shopify/:listingId/edit"
              component={ShopifyListingsV4}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/listings/goodwillfinds/:listingId"
              path="/listings/goodwillfinds/:listingId/edit"
              component={GoodwillfindsListingsV4}
            />

            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/listings/facebook/:listingId"
              path="/listings/facebook/:listingId/edit"
              component={FacebookListingsV4}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.post')}
              key="/products/:id/edit"
              path="/products/:id/edit"
              component={EditProduct}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('products.view')}
              path="/products/:id"
              component={ProductManager2}
            />
            <RestrictedRoute
              exact
              path="/manifests/reports"
              component={ManifestReports}
              // matching default roles - admin, reports_only, manager
              isPermissionGranted={permissions.includes('reports.manifests')}
            />
            <RestrictedRoute
              isPermissionGranted={matchesSomePermissions(regexMatchers.anyManifestPermission)}
              path="/manifests"
              component={ManifestRoutes}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('manifests.process')}
              exact
              path="/authentications/new"
              component={NewItemAuthentication}
            />
            <RestrictedRoute
              isPermissionGranted={includesSomePermissions('manifests.process', 'manifests.edit')}
              path="/authentications/:id"
              component={NewItemAuthentication}
            />
            <RestrictedRoute
              isPermissionGranted={includesSomePermissions('manifests.process', 'manifests.edit')}
              exact
              path="/authentications"
              component={AuthenticationManager}
            />
            <RestrictedRoute
              isPermissionGranted={
                (includesAllPermissions('manifests.edit') && !!supplierId) ||
                includesAllPermissions('manifests.override_state')
              }
              path="/manifest_scan_forms"
              component={props => <ManifestScanFormRoutes {...props} supplierId={supplierId} />}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('orders.picking')}
              path="/orders/pick_lists"
              component={PickLists}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('orders.all')}
              path="/packing"
              component={StartPacking}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('orders.all')}
              path="/orders/:id/packing"
              exact
              component={Packing}
            />
            <RestrictedRoute
              isPermissionGranted={includesAllPermissions('orders.all', 'products.view')}
              path="/orders/:id/packing/product/:productId"
              component={Packing}
            />
            <RestrictedRoute
              // Allowed for store and store admin users, but links are hidden for these roles unless UPRIGHT_LOCAL()
              isPermissionGranted={permissions.includes('orders.all')}
              path="/orders/:id"
              component={ShowOrder}
            />
            <RestrictedRoute
              // Allowed for store and store admin users, but links are hidden for these roles unless UPRIGHT_LOCAL()
              isPermissionGranted={permissions.includes('orders.all')}
              path="/orders"
              component={Orders}
            />
            <RestrictedRoute // LEGACY
              isPermissionGranted={permissions.includes('shipping.all')}
              path="/shipping"
              component={NewShipment}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('shipping.all')}
              path="/shipments/new"
              exact
              component={NewShipment}
            />
            <RestrictedRoute
              isPermissionGranted={includesAllPermissions('shipping.all', 'products.view')}
              path="/shipments/new/product/:id"
              exact
              component={NewShipment}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('shipping.all')}
              path="/shipments"
              component={Shipments}
            />
            <RestrictedRoute
              isPermissionGranted={matchesSomePermissions('settings.*')}
              path="/admin"
              component={AdminRoutes}
            />
            <RestrictedRoute
              path="/reports/productivity/operational"
              component={OperationalProductivity}
              // matching default roles - admin, reports_only, manager
              isPermissionGranted={includesAllPermissions('reports.productivity')}
            />
            <RestrictedRoute
              path="/reports/top_sales"
              component={TopSales}
              // matching default roles - admin, manager, reports_only, store_admin
              isPermissionGranted={includesAllPermissions('reports.top_sales')}
            />
            <RestrictedRoute
              path="/reports/suppliers"
              component={StoreReports}
              // matching default roles - admin, manager, reports_only, store_admin
              isPermissionGranted={includesAllPermissions('reports.supplier')}
            />
            <RestrictedRoute
              path="/reports/stores"
              component={StoreReports}
              // matching default roles - admin, manager, reports_only, store_admin
              isPermissionGranted={includesAllPermissions('reports.supplier')}
            />
            <RestrictedRoute
              path="/reports/users/:id"
              component={UserReports}
              // matching default roles - admin, reports_only, manager
              isPermissionGranted={includesSomePermissions('reports.poster_overview', 'reports.poster_targets')}
            />
            <RestrictedRoute
              path="/reports"
              component={Reports}
              // matching default roles - admin, reports_only, manager
              isPermissionGranted={matchesSomePermissions('reports.*')}
            />
            <Route path="/settings/:tab" component={Settings} />
            <Route path="/settings" component={Settings} />
            <Route path="/edit_account" component={props => <AdminNewOrEditUsers editAccount {...props} />} />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/shopify_account/:marketAccountId/bulk_review/:listingId"
              component={ListingBulkReviewShopify}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/ebay_account/:marketAccountId/bulk_review/:listingId"
              component={ListingBulkReviewEbay}
            />
            <RestrictedRoute
              isPermissionGranted={permissions.includes('listings.view')}
              exact
              path="/listings/shopgoodwill/bulk_review/:listingId"
              component={ListingBulkReviewShopgoodwill}
            />
            <RestrictedRoute isPermissionGranted exact path="/clothing-conveyor-test" component={ConveyorScreenTest} />
            <RestrictedRoute
              isPermissionGranted={config.LISTER_BOOKS() && permissions.includes('products.isbn_scanning')}
              path="/scan-books"
              exact
              component={ScanBooks}
            />
            <RestrictedRoute
              isPermissionGranted={config.LISTER_BOOKS() && permissions.includes('products.isbn_scanning')}
              path="/scan-to-list-books"
              exact
              component={ScanToListBooks}
            />
            <RestrictedRoute
              isPermissionGranted={config.LISTER_BOOKS() && permissions.includes('products.isbn_scanning')}
              path="/scan-to-list-books/new-listing"
              exact
              component={NewListingPage}
            />
            <Route
              path="/onboarding/shopgoodwill"
              render={() => (config.user.onboarded ? <Redirect to={{ pathname: '/' }} /> : <ShopGoodwillOnboarding />)}
            />
            {homeComponent && <Route exact path="/" component={homeComponent} />}
            {redirectPath && <Redirect to={redirectPath} />}
            {/* Catch all, if we couldn't determine an appropriate homeComponent or a redirectPath */}
            <NotFound />
          </Switch>
        </React.Suspense>
      </ConveyorPortProvider>
    </div>
  )
}

const TrackedApp = withRouter(PageTracker(App))
export default () => (
  <Router>
    <TrackedApp />
  </Router>
)
